<template>
  <v-container fluid>
    <div class="d-flex">
      <h2>{{ $tc('invoice', 2) }}</h2>
    </div>
    <!-- Search Bar -->
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col lg="4" md="6" cols="12">
          <vf-autocomplete
            return-object
            clearable
            multiple
            small
            :label="$tc('customer', 1)"
            v-model="searchCriteria.customers"
            :items="parentCustomers"
            item-value="id"
            item-text="name"
            prepend-inner-icon="mdi-filter"/>
        </v-col>
        <v-col lg="2" md="6" cols="12">
          <v-select
            return-object
            :label="$t('month')"
            v-model="searchCriteria.month"
            :items="monthOptions"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-trash-can"/>
        </v-col>
        <v-col lg="2" md="6" cols="12">
          <v-select
            return-object
            :label="$t('year')"
            v-model="searchCriteria.year"
            :items="yearOptions"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-trash-can"/>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <div style="margin: 4px; border: 1px solid #CCCCCC; padding: 8px;">
            <v-sparkline
              :value="getSparklineData(this.columnTotals)"
              :labels="getSparklineLabels(this.columnTotals)"
              color="primary"
              line-width="2"
              label-size="10"
              padding="12"
            ></v-sparkline>
          </div>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-col>
          <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
            <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
            {{ $t('search') }}
          </v-btn>
          <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
            <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
            {{ $t('reset') }}
          </v-btn>
        </v-col>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadInvoiceVehicleSnapshot()"
          :loading="vehicleSnapshotDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('invoice_dashboard.download_vehicle_snapshot') }}
        </v-btn>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('invoice_dashboard.download_invoices') }}
        </v-btn>
        <vf-tooltip :text="$t('invoice_dashboard.download_invoices')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <!-- Data Table -->
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        disable-pagination
        class="vf-table mt-2"
        :headers="headers"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">

        <template v-for="h in headers" v-slot:[`header.${h.value}`]>
          <span :key="h.value">
            {{h.text}}
            <span v-if="columnTotals[h.value]">({{ columnTotals[h.value] }})</span>
            <vf-tooltip v-if="h.tooltip" :text="h.tooltip">
              <v-icon small>mdi-help-circle-outline</v-icon>
            </vf-tooltip>
          </span>
        </template>

        <template #item="row">
          <tr v-if="row.item.customer.parent_id && isOpenParent(row.item.customer.parent_id)">
            <td><v-icon fixed-width>mdi-account</v-icon>{{ row.item.customer.name}} </td>
            <td>{{ row.item.invoice_model }}</td>
            <td>{{ row.item.service_period }}</td>
            <td>{{ row.item.mav }}</td>
            <td>{{ row.item.adjustment_period }}</td>
            <td>{{ row.item.mav_adjusted }}</td>
            <td>
              <div class="d-flex align-center">
                <span>${{ formatCurrency(getParentBillingAmount(row.item)) }}</span>
                <vf-tooltip v-if="isAggregatedBilling(row.item)" text="Total of child customer billings">
                  <v-icon small color="info" class="ml-1">mdi-information-outline</v-icon>
                </vf-tooltip>
                <vf-tooltip v-if="isParentBelowMinimum(row.item)" text="Billing amount is below minimum threshold">
                  <v-icon small color="amber" class="ml-1">mdi-alert</v-icon>
                </vf-tooltip>
              </div>
            </td>
            <td>{{ row.item.cav }}</td>
            <td>
              <div style="margin: 4px; border: 1px solid #CCCCCC; padding: 4px;">
                <v-sparkline
                  :value="getSparklineData(row.item)"
                  :labels="getSparklineLabels(row.item)"
                  color="primary"
                  line-width="2"
                  label-size="10"
                  padding="10"
                ></v-sparkline>
              </div>
            </td>
          </tr>
          <tr v-else-if="row.item.customer.parent_id === null" @click="toggleParent(row.item.customer.id)">
            <td><v-icon fixed-width>mdi-office-building</v-icon>{{ row.item.customer.name}} </td>
            <td>{{ row.item.invoice_model }}</td>
            <td>{{ row.item.service_period }}</td>
            <td>{{ row.item.mav }}</td>
            <td>{{ row.item.adjustment_period }}</td>
            <td>{{ row.item.mav_adjusted }}</td>
            <td>
              <div class="d-flex align-center">
                <span>${{ formatCurrency(getParentBillingAmount(row.item)) }}</span>
                <vf-tooltip v-if="isAggregatedBilling(row.item)" text="Total of child customer billings">
                  <v-icon small color="info" class="ml-1">mdi-information-outline</v-icon>
                </vf-tooltip>
                <vf-tooltip v-if="isParentBelowMinimum(row.item)" text="Billing amount is below minimum threshold">
                  <v-icon small color="amber" class="ml-1">mdi-alert</v-icon>
                </vf-tooltip>
              </div>
            </td>
            <td>{{ row.item.cav }}</td>
            <td>
              <div style="margin: 4px; border: 1px solid #CCCCCC; padding: 4px;">
                <v-sparkline
                :value="getSparklineData(row.item)"
                :labels="getSparklineLabels(row.item)"
                stroke-linecap="round"
                color="primary"
                line-width="2"
                label-size="10"
                padding="10"
                ></v-sparkline>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>
<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import { isEmpty, map, each, find, debounce } from 'lodash'
import VfTooltip from '@/components/shared/VfTooltip.vue'
import FileSaver from 'file-saver'

export default {
  name: 'ProductsIndex',
  components: {
    VfTooltip,
    VfAutocomplete
  },
  mixins: [CurrentUserMixin, RemoteDataTableMixin],
  data () {
    return {
      model: 'Invoice',
      sortBy: 'name',
      searchCriteria: this.getDefaultSearchCriteria(),
      headers: [
        { value: 'customer.name', text: this.$tc('customer', 1), sortable: false },
        { value: 'invoice_model', text: this.$t('invoice_dashboard.model'), tooltip: this.$t('invoice_dashboard.model_tooltip'), sortable: false },
        { value: 'service_period', text: this.$t('invoice_dashboard.service_period'), tooltip: this.$t('invoice_dashboard.service_period_tooltip'), sortable: false },
        { value: 'mav', text: this.$t('invoice_dashboard.mav'), tooltip: this.$t('invoice_dashboard.mav_tooltip'), sortable: false },
        { value: 'adjustment_period', text: this.$t('invoice_dashboard.adjustment_period'), tooltip: this.$t('invoice_dashboard.adjustment_period_tooltip'), sortable: false },
        { value: 'mav_adjusted', text: this.$t('invoice_dashboard.mav_adjusted'), tooltip: this.$t('invoice_dashboard.mav_adjusted_tooltip'), sortable: false },
        { value: 'billing_amount', text: 'Billing Amount', tooltip: 'Amount to be billed for this customer', sortable: true },
        { value: 'cav', text: this.$t('current_active_vehicles'), tooltip: this.$t('invoice_dashboard.current_active_vehicles_tooltip'), sortable: false },
        { value: 'history', text: this.$t('invoice_dashboard.trend'), width: '30%' }
      ],
      monthOptions: [
        { text: '1', value: '01' },
        { text: '2', value: '02' },
        { text: '3', value: '03' },
        { text: '4', value: '04' },
        { text: '5', value: '05' },
        { text: '6', value: '06' },
        { text: '7', value: '07' },
        { text: '8', value: '08' },
        { text: '9', value: '09' },
        { text: '10', value: '10' },
        { text: '11', value: '11' },
        { text: '12', value: '12' }
      ],
      columnTotals: {
        mav: 0,
        mav_adjusted: 0,
        mav_total: 0,
        pmav1: 0,
        pmav2: 0,
        pmav3: 0,
        pmav4: 0,
        pmav5: 0,
        cav: 0,
        billing_amount: 0
      },
      openParents: [], // Array to store the IDs of open parent customers
      loadRemoteDataDelayed: debounce(this.loadRemoteData, 500),
      vehicleSnapshotDownloading: false
    }
  },
  methods: {
    sortCustomers (items, index, sortDesc) {
      items.sort((a, b) => {
        if (index === 'name') {
          // Both are top level
          if (a.parent_id === null && b.parent_id === null) {
            return a.name < b.name ? -1 : 1
          }
          // A is top level, B is not
          if (a.parent_id === null) {
            // A's parent is B, A should be sorted after
            if (a.name === b.parent_name) {
              // if descending, flip to preserve hierarchy
              return this.sortDesc ? 1 : -1
            }
            return a.name < b.parent_name ? -1 : 1
          }
          // B is top level, A is not
          if (b.parent_id === null) {
            // B's parent is A, A should be sorted before
            if (b.name === a.parent_name) {
              // if descending, flip to preserve hierarchy
              return this.sortDesc ? -1 : 1
            }
            return a.parent_name < b.name ? -1 : 1
          }
          // Parents are same
          if (a.parent_id === b.parent_id) {
            return a.name < b.name ? -1 : 1
          }
          // Parents not same
          return a.parent_name < b.parent_name ? -1 : 1
        } else {
          if (this.sortDesc === false) {
            console.log('Ascending' + sortDesc)
            return a[index] < b[index] ? -1 : 1
          } else {
            console.log('Descending' + sortDesc)
            return b[index] < a[index] ? -1 : 1
          }
        }
      })
      return items
    },
    toggleParent (parent) {
      if (this.isOpenParent(parent)) {
        this.closeParent(parent)
      } else {
        this.openParent(parent)
      }
    },
    isOpenParent (parent) {
      return this.openParents.includes(parent)
    },
    openParent (parent) {
      this.openParents.push(parent)
    },
    closeParent (parent) {
      const index = this.openParents.indexOf(parent)
      if (index !== -1) {
        this.openParents.splice(index, 1)
      }
    },
    getSparklineData (item) {
      const properties = ['pmav5', 'pmav4', 'pmav3', 'pmav2', 'pmav1', 'mav']

      return properties.map(prop => Number(item[prop]) || 0)
    },
    getSparklineLabels (item) {
      return [
        item.pmav5,
        (item.pmav4 - item.pmav5 > 0 ? '+' : '') + (item.pmav4 - item.pmav5),
        (item.pmav3 - item.pmav4 > 0 ? '+' : '') + (item.pmav3 - item.pmav4),
        (item.pmav2 - item.pmav3 > 0 ? '+' : '') + (item.pmav2 - item.pmav3),
        (item.pmav1 - item.pmav2 > 0 ? '+' : '') + (item.pmav1 - item.pmav2),
        item.mav
      ]
    },
    /**
     * Map parent names to customers if available. This is a convenience function
     * to prep customers for sorting in sortCustomers.
     */
    mapParentNames (customers) {
      each(customers, a => {
        if (a.parent_id !== null) {
          const match = find(customers, b => {
            return a.parent_id === b.id
          })
          if (match !== null) {
            a.parent_name = match.name
          }
        }
      })
      return customers
    },
    getDefaultSearchCriteria () {
      const currentMonth = new Date().getMonth() + 1
      const currentMonthCode = currentMonth.toString().padStart(2, '0')
      return {
        year: { text: new Date().getFullYear(), value: new Date().getFullYear() },
        month: { text: currentMonth, value: currentMonthCode },
        customers: []
      }
    },

    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.initiateSearch()
    },

    buildSearchCriteria () {
      const searchCriteria = {
        expand: 'customer',
        fields: 'customer,customer_id,mav,mav_adjusted,mav_total,invoice_model,service_period,adjustment_period, pmav1, pmav2, pmav3, pmav4, pmav5, cav,billing_amount,cost_per_vehicle,minimum_amount,is_below_minimum',
        customer_id: this.customerContext.id,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage,
        year: this.searchCriteria.year.value,
        month: this.searchCriteria.month.value
      }

      if (!isEmpty(this.searchCriteria.customers)) {
        searchCriteria.customer_ids = map(this.searchCriteria.customers, 'id').join(',')
      }

      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      return searchCriteria
    },

    /**
     * Get total Mav, Adjustments, and Totals
     */
    getColumnTotals () {
      const columnTotals = {
        mav: 0,
        mav_adjusted: 0,
        mav_total: 0,
        pmav1: 0,
        pmav2: 0,
        pmav3: 0,
        pmav4: 0,
        pmav5: 0,
        cav: 0,
        billing_amount: 0
      }
      each(this.remoteData.items, item => {
        if (item.customer.parent_id === null) {
          columnTotals.mav += item.mav
          columnTotals.mav_adjusted += item.mav_adjusted
          columnTotals.mav_total += item.mav_total
          columnTotals.pmav1 = parseInt(columnTotals.pmav1) + parseInt(item.pmav1)
          columnTotals.pmav2 = parseInt(columnTotals.pmav2) + parseInt(item.pmav2)
          columnTotals.pmav3 = parseInt(columnTotals.pmav3) + parseInt(item.pmav3)
          columnTotals.pmav4 = parseInt(columnTotals.pmav4) + parseInt(item.pmav4)
          columnTotals.pmav5 = parseInt(columnTotals.pmav5) + parseInt(item.pmav5)
          columnTotals.cav = parseInt(columnTotals.cav) + parseInt(item.cav)

          // Handle billing amounts for both parent-level and child-level billing
          if (item.billing_amount > 0) {
            // Parent-level billing: use parent's billing amount
            columnTotals.billing_amount += parseFloat(item.billing_amount)
          } else {
            // Check if this parent has child-level billing
            let childBillingTotal = 0
            this.remoteData.items.forEach(childItem => {
              if (childItem.customer.parent_id === item.customer.id && childItem.billing_amount > 0) {
                childBillingTotal += parseFloat(childItem.billing_amount)
              }
            })
            // Add child billing total if found, otherwise parent's 0
            columnTotals.billing_amount += childBillingTotal > 0 ? childBillingTotal : parseFloat(item.billing_amount || 0)
          }
        }
      })
      this.columnTotals = columnTotals
    },

    /**
     * Wrapper to invoke debounced version of loadRemoteData
     */
    refreshResults () {
      this.remoteData.isBusy = true
      this.remoteData.rowsPerPage = 500
      this.loadRemoteDataDelayed()
      this.remoteData.items = this.mapParentNames(this.remoteData.items)
    },

    async loadRemoteData () {
      this.remoteData.isBusy = true
      try {
        const searchCriteria = this.buildSearchCriteria()
        const collection = await this.$orm(this.model).findAll(searchCriteria)
        this.remoteData.currentPage = collection.getCurrentPage()
        this.remoteData.totalRows = collection.getTotalItemCount()
        this.remoteData.items = this.transformRemoteData(collection.getItems())
        this.remoteData.items = this.sortCustomers(this.remoteData.items, this.sortBy, this.sortDesc)
        this.getColumnTotals()
      } catch (error) {
        await this.$store.dispatch('globalMessagingModule/showModal', {
          message: this.$t('network_error_message')
        })
        throw (error)
      } finally {
        this.remoteData.isBusy = false
        this.remoteData.isLoaded = true
      }
    },

    /**
     * Customized version of downloadRemoteDataCsv specifically for Invoice Vehicles Snapshot
     *
     * @return null
     */
    async downloadInvoiceVehicleSnapshot () {
      // Update the UI to show the spinner on the download button
      this.vehicleSnapshotDownloading = true

      // Fire an analytics event
      this.$gtag.event('download', { event_category: this.model })

      const request = {
        url: this.$orm(this.model).endpoint,
        method: 'GET',
        headers: {
          Accept: 'text/csv'
        },
        params: {
          date: this.searchCriteria.year.value + '-' + this.searchCriteria.month.value,
          type: 'vehicle-snapshot'
        },
        responseType: 'blob' // important
      }

      try {
        const response = await this.$httpAdapter(request)
        // Parse the file name from the content-disposition header
        const headerLine = response.headers['content-disposition']
        const startFileNameIndex = headerLine.indexOf('"') + 1
        const endFileNameIndex = headerLine.lastIndexOf('"')
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex)
        FileSaver.saveAs(new Blob([response.data]), fileName)
      } catch (error) {
        if (error) {
          await this.$store.dispatch('globalMessagingModule/showModal', {
            message: this.$t('network_error_message')
          })
          throw (error)
        }
      } finally {
        this.vehicleSnapshotDownloading = false
      }
    },

    /**
     * Format currency for display
     */
    formatCurrency (amount) {
      if (amount === null || amount === undefined) {
        return '0.00'
      }
      return Number(amount).toFixed(2)
    },

    /**
     * Calculate billing amount for parent customers, handling both scenarios:
     * 1. Parent-level billing (parent has billing settings)
     * 2. Child-level billing (parent shows sum of children)
     */
    getParentBillingAmount (item) {
      // If this is a child customer, return its own billing
      if (item.customer.parent_id !== null) {
        return item.billing_amount
      }

      // For parent customers, check if they have their own billing amount
      if (item.billing_amount > 0) {
        // Parent-level billing: use parent's billing amount
        return item.billing_amount
      }

      // Parent has no billing, check if children have billing (child-level billing scenario)
      let childrenTotal = 0
      this.remoteData.items.forEach(childItem => {
        if (childItem.customer.parent_id === item.customer.id) {
          childrenTotal += parseFloat(childItem.billing_amount || 0)
        }
      })

      // Return children's total if found, otherwise parent's 0
      return childrenTotal > 0 ? childrenTotal : item.billing_amount
    },

    /**
     * Check if parent billing amount is aggregated from children
     */
    isAggregatedBilling (item) {
      // Only applies to parent customers
      if (item.customer.parent_id !== null) {
        return false
      }

      // If parent has its own billing, it's not aggregated
      if (item.billing_amount > 0) {
        return false
      }

      // Check if children have billing
      let hasChildBilling = false
      this.remoteData.items.forEach(childItem => {
        if (childItem.customer.parent_id === item.customer.id && childItem.billing_amount > 0) {
          hasChildBilling = true
        }
      })

      return hasChildBilling
    },

    /**
     * Check if billing amount is below minimum threshold
     * Handles both regular items and parent minimum checks for aggregated child billing
     */
    isParentBelowMinimum (item) {
      // For child customers, use their own is_below_minimum flag
      if (item.customer.parent_id !== null) {
        return item.is_below_minimum
      }

      // For parent customers with their own billing, use their flag
      if (parseFloat(item.billing_amount || 0) > 0) {
        return item.is_below_minimum
      }

      // For parent customers with $0 billing (child-level billing scenario)
      // Check if aggregated child billing is below parent's minimum
      const minimumAmount = parseFloat(item.minimum_amount || 0)

      if (minimumAmount > 0) {
        let childrenTotal = 0
        this.remoteData.items.forEach(childItem => {
          if (childItem.customer.parent_id === item.customer.id) {
            const childBilling = parseFloat(childItem.billing_amount || 0)
            childrenTotal += childBilling
          }
        })


        return childrenTotal > 0 && childrenTotal < minimumAmount
      }

      return false
    },

  },
  computed: {
    yearOptions () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = currentYear; i >= 2000; i--) {
        years.push({ text: i, value: i })
      }
      return years
    },
    parentCustomers () {
      return this.customers.filter(customer => customer.parent_id === null)
    }
  },
  created () {
    this.refreshResults()
  }
}
</script>

<style lang="scss" scoped>

.v-data-table > .v-data-table__wrapper > table {
  border-spacing: 0 0.5rem;
}

</style>
